import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiPost } from '@/lib/api';

interface RegenerateDraftResponse {
  ok: boolean;
  brief_id: string;
  workflow_id: string;
}

/**
 * useRegenerateDraft — POSTs to `/v1/briefs/:id/regenerate-draft`. Starts the
 * `briefV2DraftOnlyWorkflow` which re-runs draft → critique → ground → persist
 * using the persisted `planned_sections` snapshot, skipping the 8-15 min
 * plan-sections step. Invalidates brief caches so the UI reflects the fresh
 * content once the workflow finishes.
 *
 * Shown when the brief has any warning whose `code` starts with
 * `BRIEF_DRAFT_DEGRADED` — see the warnings banner in `briefs/[id].tsx`.
 */
export function useRegenerateDraft(briefId: string | undefined) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiPost<RegenerateDraftResponse>(`/v1/briefs/${briefId}/regenerate-draft`, {}),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['brief', briefId] });
      void qc.invalidateQueries({ queryKey: ['briefs'] });
    },
  });
}
